import { useState, useEffect } from "react";
import { useTheme, Typography, Button } from "@mui/material";
import Box from "@mui/material/Box";
import { StartScreenRoot } from "./StartScreen.styles";
import { BackgroundMedia } from "../BackgroundMedia";
import { WelcomeAndIntructionsPopUp } from "../WelcomeAndIntructionsPopUp";
import interbionLogo from "../../assets/logo_interbion.png";

export const StartScreen = ({
  onHandleClose,
  videoInitialized,
  arcwareApplication,
  environmentLoading,
  onSendResOnStart,
  show,
  onHandleMenuToggle,
  menuOpen
}) => {
  const theme = useTheme();

  const streamingSession = localStorage.getItem("streamingSession");

  const clickOnAction = (descriptor) => {
    arcwareApplication?.emitUIInteraction(descriptor);
  };

  const [isVisible, setIsVisible] = useState(show); // controls DOM presence
  const [isShown, setIsShown] = useState(show); // controls opacity

  useEffect(() => {
    let timeout;
    if (show) {
      // show immediately
      setIsVisible(true);
      requestAnimationFrame(() => setIsShown(true));
    } else {
      // start fade out
      setIsShown(false);
      // remove after 1s (match CSS transition)
      timeout = setTimeout(() => setIsVisible(false), 1000);
    }
    return () => clearTimeout(timeout);
  }, [show]);

  const handleStart = () => {
    const orientation =
      window.innerWidth > window.innerHeight
        ? { zoom: "0" }
        : { zoom: "-37.5" };

    clickOnAction(orientation);
    onHandleClose(false, "click");
    onHandleMenuToggle(!menuOpen);

    // Send the resolution command after 1s
    const timeout = setTimeout(() => {
      onSendResOnStart(true);
    }, 750);

    if (!streamingSession) localStorage.setItem("streamingSession", new Date());

    // Clean up timer if component unmounts early
    return () => clearTimeout(timeout);
  };

  if (!isVisible) return null; // fully unmounted from DOM after fade

  return (
    <StartScreenRoot
      sx={{ opacity: isShown ? 1 : 0, pointerEvents: isShown ? "all" : "none" }}
    >
      <BackgroundMedia />
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: { xs: "2rem", md: "1rem", xl: "2rem" },
        }}
      >
        <Box
          sx={{
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Typography
            className="instructions__title"
            variant="h3"
            sx={{
              textAlign: "left",
              position: "relative",
              lineHeight: 1,
              marginBottom: 1,
              fontWeight: 300,
              fontSize: { xs: "2.25rem", lg: "2.75rem" },
            }}
          >
            New office project for
          </Typography>
          <Box
            component="img"
            src={interbionLogo}
            alt="Fallback"
            sx={{
              width: { xs: "70%", lg: "auto" },
              height: "auto",
              display: "block",
              zIndex: 1,
              [theme.breakpoints.down("lg")]: {
                "@media (orientation: landscape)": {
                  width: "20rem",
                },
              },
            }}
          />
        </Box>
        <WelcomeAndIntructionsPopUp />
        <Button
          sx={{
            color: theme?.palette?.companyColors?.secondary,
            background: theme?.palette?.companyColors?.accent,
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            gap: "1rem",
            margin: "3rem 0",
            textTransform: "none",
            fontSize: { xs: "1.5rem", lg: "1.75rem" },
            padding: { xs: "1.25rem 5rem", xl: "1.25rem 7.5rem" },
            transition: "all 0.3s ease-in-out",
            pointerEvents: !videoInitialized ? "none" : "all",
            cursor: !videoInitialized ? "default" : "pointer",
            opacity: !videoInitialized ? 0.5 : 1,
            borderRadius: theme?.borderRadius?.small,
            boxShadow: "0px 0px 5px rgba(0,0,0,0.25)",
            fontWeight: 400,
            "&:hover": {
              background: theme?.palette?.companyColors?.secondary,
              color: theme?.palette?.companyColors?.accent,
            },
            [theme.breakpoints.down("lg")]: {
              "@media (orientation: landscape)": {
                margin: "2rem 0",
              },
            },
            "& svg": {
              transition: "all 0.3s ease-in-out",
            },
          }}
          onClick={() => {
            if (!videoInitialized) return;
            handleStart();
          }}
        >
          {!videoInitialized && !environmentLoading ? (
            <>
              Loading
              <Box component="span" className="start-screen__loading__dot">
                .
              </Box>
              <Box component="span" className="start-screen__loading__dot">
                .
              </Box>
              <Box component="span" className="start-screen__loading__dot">
                .
              </Box>
            </>
          ) : (
            <Box component="span">{`${
              streamingSession ? "Resume" : "Start"
            }`}</Box>
          )}
        </Button>
      </Box>
    </StartScreenRoot>
  );
};
